import React from 'react';
import RecoveryBlockersPanel from '../components/RecoveryBlockersPanel';
import RecoveryChecklistPanel from '../components/RecoveryChecklistPanel';
import RecoveryMismatchSummaryPanel from '../components/RecoveryMismatchSummaryPanel';
import RecoveryModeChangePanel from '../components/RecoveryModeChangePanel';
import RecoveryModeHistoryPanel from '../components/RecoveryModeHistoryPanel';
import RecoveryReadinessPanel from '../components/RecoveryReadinessPanel';
import RecoveryRiskMetricsPanel from '../components/RecoveryRiskMetricsPanel';
import RecoveryRollbackRecommendationPanel from '../components/RecoveryRollbackRecommendationPanel';
import RecoveryRolloutModeCard from '../components/RecoveryRolloutModeCard';
import { useRecoveryRolloutData } from '../hooks/useRecoveryRolloutData';

const RecoveryRolloutDashboardPage = () => {
  const {
    currentMode,
    targetMode,
    setTargetMode,
    checklistType,
    setChecklistType,
    readiness,
    checklist,
    blockers,
    rollbackRecommendation,
    overview,
    mismatchRows,
    riskMetrics,
    transitionExamples,
    validationResult,
    modeHistory,
    runValidation,
    runModeChange,
    validateMutation,
    changeModeMutation,
    refreshAll,
    isLoading,
    error,
  } = useRecoveryRolloutData();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto w-full max-w-[1700px] space-y-4 p-4 lg:p-6">
        <header className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Recovery AI v2 Rollout</div>
          <h1 className="mt-1 text-2xl font-semibold">Recovery Rollout Dashboard</h1>
          <p className="mt-1 text-sm text-slate-400">
            Panel internal untuk validasi readiness, guardrails promosi mode, dan kontrol rollback Recovery AI v2.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={refreshAll}
              className="rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-slate-200"
            >
              Refresh
            </button>
            <span className="rounded-md border border-slate-800 bg-slate-950/70 px-2 py-1 text-[11px] text-slate-400">
              Overview comparisons: {overview?.total_comparisons ?? 0}
            </span>
          </div>
        </header>

        {isLoading ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-400">Memuat data rollout...</div>
        ) : null}

        {error ? (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
            {error?.message || 'Gagal memuat data recovery rollout dashboard'}
          </div>
        ) : null}

        <RecoveryRolloutModeCard
          currentMode={currentMode}
          targetMode={targetMode}
          readiness={readiness}
          lastUpdated={readiness?.evaluatedAt}
        />

        <div className="grid gap-4 xl:grid-cols-[1fr_380px]">
          <div className="space-y-4">
            <RecoveryReadinessPanel readiness={readiness} />
            <RecoveryRiskMetricsPanel metrics={riskMetrics} />
            <RecoveryMismatchSummaryPanel rows={mismatchRows} />
            <RecoveryBlockersPanel blockers={blockers?.blockers || []} warnings={blockers?.warnings || []} />
            <RecoveryChecklistPanel
              checklist={checklist}
              checklistType={checklistType}
              onChangeChecklistType={setChecklistType}
            />
          </div>

          <div className="space-y-4">
            <RecoveryRollbackRecommendationPanel recommendation={rollbackRecommendation} />
            <RecoveryModeChangePanel
              currentMode={currentMode}
              targetMode={targetMode}
              setTargetMode={setTargetMode}
              validationResult={validationResult}
              onValidate={runValidation}
              onChangeMode={runModeChange}
              validating={validateMutation.isPending}
              changing={changeModeMutation.isPending}
            />

            <section className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-100">Transition Rules (Contoh)</h3>
              <ul className="space-y-2 text-xs text-slate-300">
                {(transitionExamples || []).map((item, idx) => (
                  <li key={`example-${idx}`} className="rounded-md border border-slate-800 bg-slate-950/70 px-2 py-1.5">
                    <span className="font-semibold text-slate-100">{item.currentMode} -&gt; {item.targetMode}</span>
                    <span className="ml-2 text-slate-400">({item.expectedResult})</span>
                    <p className="mt-1 text-[11px] text-slate-400">{item.reason}</p>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>

        <RecoveryModeHistoryPanel rows={modeHistory} />
      </div>
    </div>
  );
};

export default RecoveryRolloutDashboardPage;
